import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ticketShowService from "../services/ticketShowService";
import { usePermissions } from "../hooks/usePermissions";
import { useAuth } from "../contexts/AuthContext";
import { CHANGE_EVENT } from "../lib/inspectionSyncEngine";
import TicketCertificatePanel from "../components/TicketCertificatePanel";
import ticketsService from "../services/ticketsService";
import { certificateTypeFromServiceName } from "../utils/certificateTypes";
import styles from "./TicketShow.module.css";



// Helper functions for status fallback
const getStatusColor = (statusId) => {
  const statusColors = {
    1: "#5cb85c", // New - Green
    2: "#f0ad4e", // In Action - Orange  
    3: "#5bc0de", // Request - Light Blue
    4: "#5bc0de", // Request - Light Blue
    5: "#2e8b57", // Completed - Dark Green
    6: "#d9534f", // Cancelled/Declined - Red
    7: "#17a2b8", // Contr. Completed - Teal
    8: "#6f42c1", // Building Set Up - Purple
  };
  return statusColors[statusId] || '#6c757d';
};

const getStatusName = (statusId) => {
  const statusNames = {
    1: "New",
    2: "In Action", 
    3: "Request",
    4: "Request",
    5: "Completed",
    6: "Cancelled",
    7: "Contr. Completed",
    8: "Building Set Up",
  };
  return statusNames[statusId] || 'Unknown';
};

const TicketShow = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { user, isClient, isContractor, isGuardian, getContractorContactId } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [ticketNotes, setTicketNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is CLIENT role - hide ticket notes section for clients
  const isClientUser = isClient();
  // Check if user is CONTRACTOR role
  const isContractorUser = isContractor();
  // Check if user is GUARDIAN role - show simplified view
  const isGuardianUser = isGuardian();



  useEffect(() => {
    loadTicketData();
  }, [id]); // loadTicketData is stable, no need to include in deps

  // Refresh the ticket (Documents + Notes) when an offline draft finishes
  // syncing in the background, so newly-synced items appear without a manual
  // page refresh.
  useEffect(() => {
    let t = null;
    const onChange = () => {
      if (typeof navigator !== "undefined" && !navigator.onLine) return;
      // Debounce: the engine emits several change events during a sync run.
      if (t) clearTimeout(t);
      t = setTimeout(() => {
        loadTicketData();
      }, 600);
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      if (t) clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadTicketData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try to get real data first
      // Use Promise.allSettled so notes failure doesn't block ticket display
      const [ticketResult, notesResult] = await Promise.allSettled([
        ticketShowService.getTicket(id),
        ticketShowService.getTicketNotes(id),
      ]);

      // If the ticket request itself failed, re-throw the error
      if (ticketResult.status === 'rejected') {
        throw ticketResult.reason;
      }

      const ticketData = ticketResult.value;
      const notesData = notesResult.status === 'fulfilled' ? notesResult.value : [];

      if (notesResult.status === 'rejected') {
        console.warn('Failed to load ticket notes, showing ticket without notes:', notesResult.reason);
      }

      // Check if we got valid data
      if (ticketData && ticketData.id) {
        console.log('=== FRONTEND TICKET DATA DEBUG ===');
        console.log('Full ticket data:', ticketData);
        console.log('Member data:', ticketData?.member);
        console.log('Member ID:', ticketData?.member_id);
        console.log('Assigned Member data:', ticketData?.assignedMember);
        console.log('Contractor Contact data:', ticketData?.contractorContact);
        console.log('Contractor Contact User data:', ticketData?.contractorContact?.user);
        console.log('🛡️ Guardian data received:', ticketData?.guardian);
        console.log('🛡️ Guardian ID:', ticketData?.guardian_id);
        console.log('🛡️ Guardian User data (nested):', ticketData?.guardian?.user);
        console.log('🛡️ Guardian User data (root level):', ticketData?.guardianUser);
        console.log('🛡️ Guardian first_name:', ticketData?.guardian?.first_name);
        console.log('🛡️ Guardian last_name:', ticketData?.guardian?.last_name);
        console.log('🛡️ Guardian full_name:', ticketData?.guardian?.full_name);
        console.log('🛡️ Guardian email:', ticketData?.guardian?.email);
        console.log('🛡️ Guardian phone_number:', ticketData?.guardian?.phone_number);
        console.log('🎫 Status data received:', {
          status_id: ticketData?.status_id,
          status_object: ticketData?.status,
          status_name: ticketData?.status?.name,
          status_color: ticketData?.status?.color
        });

        // Map guardianUser from root level to guardian.user if needed
        if (ticketData.guardianUser && !ticketData.guardian?.user) {
          console.log('🔄 Mapping guardianUser from root level to guardian.user');
          if (!ticketData.guardian) {
            ticketData.guardian = {};
          }
          ticketData.guardian.user = ticketData.guardianUser;
          // Also add fields directly to guardian for backward compatibility
          ticketData.guardian.first_name = ticketData.guardianUser.first_name;
          ticketData.guardian.last_name = ticketData.guardianUser.last_name;
          ticketData.guardian.email = ticketData.guardianUser.email;
          ticketData.guardian.phone_number = ticketData.guardianUser.phone_number;
          ticketData.guardian.full_name = ticketData.guardianUser.first_name && ticketData.guardianUser.last_name
            ? `${ticketData.guardianUser.first_name} ${ticketData.guardianUser.last_name}`.trim()
            : null;
          console.log('✅ Mapped guardianUser to guardian.user:', ticketData.guardian.user);
        }

        // Also handle case where guardian exists but user is at root level
        if (ticketData.guardian && ticketData.guardianUser && !ticketData.guardian.user) {
          ticketData.guardian.user = ticketData.guardianUser;
          ticketData.guardian.first_name = ticketData.guardianUser.first_name;
          ticketData.guardian.last_name = ticketData.guardianUser.last_name;
          ticketData.guardian.email = ticketData.guardianUser.email;
          ticketData.guardian.phone_number = ticketData.guardianUser.phone_number;
          ticketData.guardian.full_name = ticketData.guardianUser.first_name && ticketData.guardianUser.last_name
            ? `${ticketData.guardianUser.first_name} ${ticketData.guardianUser.last_name}`.trim()
            : null;
          console.log('✅ Mapped guardianUser to existing guardian.user');
        }

        setTicket(ticketData);
        setTicketNotes(notesData || []);
      } else {
        console.error('Invalid ticket data received:', ticketData);
        throw new Error('Invalid ticket data received');
      }
    } catch (err) {
      console.error("Error loading ticket data:", err);
      
      // Check if error is 403 (Forbidden) - client doesn't have access to this ticket
      // Support both axios-style errors (err.response.status) and fetch-style errors (err.status)
      const statusCode = err.response?.status || err.status;
      const errorMessage = err.response?.data?.message || err.message || "You do not have permission to view this ticket";
      
      if (statusCode === 403) {
        console.log('❌ [TICKET SHOW] Access denied (403):', errorMessage);
        
        // If user is CLIENT, redirect to dashboard
        if (isClientUser) {
          toast.error(errorMessage);
          navigate('/dashboard', { replace: true });
          return;
        }
        
        // For other users, show error message
        setError(errorMessage);
        toast.error(errorMessage);
      } else {
        // Other errors
        setError('Failed to load ticket data. Please try again.');
        
        // Only use mock data in development
        if (process.env.NODE_ENV === 'development') {
          console.log('Using mock data in development mode');
          const mockTicket = ticketShowService.getMockTicketData();
          const mockNotes = ticketShowService.getMockNotesData();
          setTicket(mockTicket);
          setTicketNotes(mockNotes);
          toast.warning("Using mock data for development");
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditTicket = () => {
    navigate(`/tickets/${id}/edit`);
  };

  // Staff toggle: marking a ticket as a certificate renewal enables the certificate
  // upload panel and the review queue for it.
  const handleToggleCertificateRenewal = async (checked) => {
    try {
      const res = await ticketsService.setCertificateRenewal(id, checked);
      if (res?.success) {
        toast.success(
          checked
            ? "Marked as a certificate renewal"
            : "No longer a certificate renewal"
        );
        loadTicketData(); // refresh so the upload panel appears/disappears
      } else {
        throw new Error(res?.message || "Update failed");
      }
    } catch (err) {
      toast.error(
        err.response?.data?.message || "Failed to update certificate renewal setting"
      );
    }
  };

  const handleDeleteTicket = async () => {
    if (!window.confirm("Are you sure you want to remove?")) {
      return;
    }

    try {
      await ticketShowService.deleteTicket(id);
      toast.success("Ticket successfully deleted");
      navigate("/tickets");
    } catch (err) {
      toast.error("Failed to delete ticket");
    }
  };

  // Handle document download
  const handleDownloadDocument = async (document) => {
    try {
      console.log("Downloading document:", document);
      await ticketShowService.downloadDocument(document.id, document.name);
      toast.success("Document downloaded successfully");
    } catch (err) {
      console.error("Error downloading document:", err);
      toast.error("Failed to download document");
    }
  };

  // Handle playing video document
  const handlePlayDocument = (document) => {
    const playUrl = ticketShowService.getPlayUrl(document.id);
    window.open(playUrl, '_blank');
  };

  // Check if document is a video file
  const isVideoFile = (doc) => {
    return ticketShowService.isVideoFile(doc.file || doc.name);
  };

  // Handle document removal with related records
  const handleRemoveDocument = async (document) => {
    if (!window.confirm(`Are you sure you want to remove "${document.name}" and all related records?`)) {
      return;
    }

    try {
      await ticketShowService.deleteDocument(document.id);
      toast.success("Document and related records successfully removed");
      loadTicketData(); // Reload to update the list
    } catch (err) {
      console.error("Error removing document:", err);
      toast.error("Failed to remove document");
    }
  };

  // Handle note removal with related records
  const handleRemoveNote = async (note) => {
    if (!window.confirm(`Are you sure you want to remove Note #${note.id} and all related records?`)) {
      return;
    }

    try {
      await ticketShowService.deleteNote(note.id);
      toast.success("Note and related records successfully removed");
      loadTicketData(); // Reload to update the list
    } catch (err) {
      console.error("Error removing note:", err);
      toast.error("Failed to remove note");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";

    // Some ticket endpoints already return dates pre-formatted as DD/MM/YYYY.
    // new Date() would read those as US MM/DD/YYYY (or invalid for days >12),
    // so return them unchanged.
    if (typeof dateString === "string" && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(dateString)) {
      return dateString;
    }

    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return `${date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric"
    })} ${date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit"
    })}`;
  };



  const formatTime = (dateString) => {
    if (!dateString) return "00:00";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false
    });
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}>Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.error}>Error: {error}</div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className={styles.errorContainer}>
        <div className={styles.error}>Ticket not found</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <span className={styles.breadcrumbItem} onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer', color: '#337ab7' }}>Dashboard</span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span
          className={styles.breadcrumbItem}
          onClick={() => {
            // Navigate based on user role
            if (isClientUser) {
              // Clients go to their tickets page
              navigate('/client-tickets');
            } else if (isContractorUser) {
              // Contractors go to their calendar (their main dashboard)
              const contractorId = getContractorContactId();
              if (contractorId) {
                navigate(`/contractors/contacts/${contractorId}/calendar`);
              } else {
                navigate('/dashboard');
              }
            } else {
              // All other users go to the main tickets list
              navigate('/tickets');
            }
          }}
          style={{ cursor: 'pointer', color: '#337ab7' }}
        >
          Tickets
        </span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.breadcrumbActive}>Ticket {ticket.id}</span>
      </div>

      {/* Action Buttons - Hidden for guardians (Laravel: guardians can only view, not edit) */}
      {!isGuardianUser && (hasPermission('tickets.create') || hasPermission('tickets.edit')) && (
        <div className={styles.actionButtons}>
          <button
            style={{ backgroundColor: "#1B2046" }}
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={handleEditTicket}
          >
            <i className="glyphicon glyphicon-edit"></i> Edit Ticket
          </button>
          {/* <button
            className={`${styles.btn} ${styles.btnDanger}`}
            onClick={handleDeleteTicket}
          >
            Delete
          </button> */}
        </div>
      )}

      <div className={styles.row}>
        {/* Left Column */}
        <div className={styles.leftColumn}>
          {/* Ticket Details */}
          <div className={styles.panel}>
            <div className={styles.panelTitle}>
              <h3>Ticket details</h3>
            </div>
            <div className={styles.panelGroup}>
              <dl className={styles.detailsList}>
                <div>
                  <dt>ID:</dt>
                  <dd>{ticket.id}</dd>
                </div>
                {/* Client view and Guardian view - Hidden for guardians (staff-only fields) */}
                {!isGuardianUser && (
                  <>
                    <div>
                      <dt>Can client see ticket:</dt>
                      <dd>{ticket.client_view ? "Yes" : "No"}</dd>
                    </div>

                    <div>
                      <dt>Can guardian see ticket:</dt>
                      <dd>{ticket.guardian_view ? "Yes" : "No"}</dd>
                    </div>
                  </>
                )}

                <div>
                  <dt>Issue on property:</dt>
                  <dd>
                    {ticket.property?.name}
                    {ticket.property?.property_id && (
                      <span className={styles.propertyId}>
                        (Property ID: {ticket.property.property_id})
                      </span>
                    )}
                    <br />
                    {ticket.property?.address_1 && (
                      <div>{ticket.property.address_1}</div>
                    )}
                    {ticket.property?.address_2 && (
                      <div>{ticket.property.address_2}</div>
                    )}
                    {ticket.property?.address_3 && (
                      <div>{ticket.property.address_3}</div>
                    )}
                    {ticket.property?.postcode && (
                      <div>{ticket.property.postcode}</div>
                    )}
                  </dd>
                </div>

                {ticket.ticketable_type === "App\\Rooms\\Room" && (
                  <div>
                    <dt>With room:</dt>
                    <dd>{ticket.ticketable?.name || "Room"}</dd>
                  </div>
                )}

                <div>
                  <dt>Type:</dt>
                  <dd>{ticket.type?.name || "-"}</dd>
                </div>

                <div>
                  <dt>Urgency:</dt>
                  <dd>
                    {ticket.urgency?.name ? (
                      <span
                        className={`${styles.label} ${
                          styles[`urgency${ticket.urgency.name.toLowerCase()}`]
                        }`}
                      >
                        {ticket.urgency.name}
                      </span>
                    ) : (
                      "-"
                    )}
                  </dd>
                </div>

                <div>
                  <dt>Status:</dt>
                  <dd>
                    <span
                      className={styles.label}
                      style={{ backgroundColor: ticket.status?.color || getStatusColor(ticket.status_id) || '#6c757d' }}
                    >
                      {ticket.status?.name || getStatusName(ticket.status_id) || 'Unknown'}
                    </span>
                  </dd>
                </div>

                {/* Certificate renewal — staff only, and only on certification ticket types.
                    Toggling it enables/disables the certificate upload panel below. */}
                {!isGuardianUser &&
                  !isClientUser &&
                  !isContractorUser &&
                  !!certificateTypeFromServiceName(ticket.type?.name) && (
                    <div>
                      <dt>Certificate Renewal:</dt>
                      <dd
                        title="Only renewal tickets show the certificate upload panel and go through the review queue."
                      >
                        <label
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            cursor: "pointer",
                            fontWeight: 400,
                            margin: 0,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={
                              ticket.is_certificate_renewal === true ||
                              ticket.is_certificate_renewal === 1 ||
                              ticket.is_certificate_renewal === "1"
                            }
                            onChange={(e) =>
                              handleToggleCertificateRenewal(e.target.checked)
                            }
                            style={{
                              marginRight: 8,
                              transform: "scale(1.1)",
                              cursor: "pointer",
                            }}
                          />
                          <span style={{ fontSize: 13, color: "#6b7a8a" }}>
                            Enables the certificate upload panel and internal review
                          </span>
                        </label>
                      </dd>
                    </div>
                  )}

                {ticket.description && (
                  <div>
                    <dt>Description:</dt>
                    <dd className={styles.description}>{ticket.description}</dd>
                  </div>
                )}

                {/* Staff-only fields - Hidden for guardians, clients, and contractors */}
                {!isGuardianUser && !isClientUser && !isContractorUser && ticket.internal_note && (
                  <div>
                    <dt>Internal note:</dt>
                    <dd className={styles.description}>
                      {ticket.internal_note}
                    </dd>
                  </div>
                )}

                {!isGuardianUser && ticket.equipment_purchased && (
                  <div>
                    <dt>Equipment purchased:</dt>
                    <dd className={styles.description}>
                      {ticket.equipment_purchased}
                    </dd>
                  </div>
                )}

                {!isGuardianUser && ticket.cost_of_works && (
                  <div>
                    <dt>Cost of works:</dt>
                    <dd>£{ticket.cost_of_works}</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Documents */}
          <div className={styles.panel}>
            <div className={styles.panelTitle} style={{ border: "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <h3>Documents</h3>
                {/* Add documents button - Hidden for clients (requires documents.tickets.create permission in Laravel) */}
                {!isClientUser && (
                  <button
                    type="button"
                    style={{ backgroundColor: "#1B2046" }}
                    className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      console.log('Add documents button clicked, navigating to:', `/tickets/${id}/documents/create`);
                      navigate(`/tickets/${id}/documents/create`);
                    }}
                  >
                    <i className="glyphicon glyphicon-plus"></i> Add documents
                  </button>
                )}
              </div>
            </div>
            <div className={styles.panelGroup}>
              <div className={styles.tableResponsive}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Uploaded</th>
                      {/* Guardian visible column - Hidden for guardians */}
                      {!isGuardianUser && <th>Guardian visible</th>}
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticket.documents && ticket.documents.length > 0 ? (
                      ticket.documents.map((doc) => (
                        <tr key={doc.id}>
                          <td>
                            <a
                              style={{
                                color: "#417ABA",
                                fontFamily: "Montserrat",
                                fontSize: "14px",
                                fontWeight: "400",
                              }}
                              href="#"
                              className={styles.documentLink}
                              onClick={(e) => {
                                e.preventDefault();
                                handleDownloadDocument(doc);
                              }}
                            >
                              {doc.name}
                            </a>
                          </td>
                          <td>{formatDate(doc.created_at)}</td>
                          {/* Guardian visible column - Hidden for guardians */}
                          {!isGuardianUser && <td>{doc.visible_to_guardian ? "Yes" : "No"}</td>}
                          <td>
                            <div className={styles.actionButtons}>
                              {/* Play button for video files */}
                              {hasPermission('tickets.show') && isVideoFile(doc) && (
                                <button
                                  type="button"
                                  className={`${styles.btn} ${styles.btnDanger} ${styles.btnSm}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handlePlayDocument(doc);
                                  }}
                                  title="Play video"
                                >
                                  ▶ Play
                                </button>
                              )}
                              {hasPermission('tickets.show') && (
                                <button
                                  type="button"
                                  className={`${styles.btn} ${styles.btnWarning} ${styles.btnSm}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleDownloadDocument(doc);
                                  }}
                                  title="Download document"
                                >
                                  Download
                                </button>
                              )}
                              {/* Remove button - Hidden for guardians and clients (requires tickets.edit in Laravel) */}
                              {!isGuardianUser && !isClientUser && (hasPermission('tickets.delete') || hasPermission('tickets.edit')) && (
                                <button
                                  type="button"
                                  className={`${styles.btn} ${styles.btnDanger} ${styles.btnSm}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemoveDocument(doc);
                                  }}
                                  title="Remove document with all related records"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={isGuardianUser ? "3" : "4"} className={styles.noData}>
                          No documents
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Notes - Visible to all roles (matching Laravel behavior) */}
          <div className={styles.panel}>
            <div className={styles.panelTitle} style={{ border: "none" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <h3>Ticket notes</h3>
                <button
                  type="button"
                  style={{ backgroundColor: "#1B2046" }}
                  className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('Add notes button clicked, navigating to:', `/tickets/${id}/notes/add`);
                    navigate(`/tickets/${id}/notes/add`);
                  }}
                >
                  <i className="glyphicon glyphicon-plus"></i> Add notes
                </button>

              </div>
            </div>
            <div className={styles.panelGroup}>
              <div className={styles.tableResponsive}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Description</th>
                      <th>Note updated at</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ticketNotes && ticketNotes.length > 0 ? (
                      ticketNotes.map((note) => (
                        <tr key={note.id}>
                          <td>
                            <button
                              style={{
                                color: "#417ABA",
                                fontFamily: "Montserrat",
                                fontSize: "14px",
                                fontWeight: "400",
                                background: "none",
                                border: "none",
                                padding: 0,
                                textDecoration: "underline",
                                cursor: "pointer",
                              }}
                              className={styles.noteLink}
                              onClick={() => navigate(`/tickets/${ticket.id}/notes/${note.id}`)}
                            >
                              Note #{note.id}
                            </button>
                          </td>
                          <td>
                            {note.updated_at && (
                              <span className={styles.noteInfo} style={{ width: "100%" }}>
                                {formatDateTime(note.updated_at)}
                                {note.user && ` by ${note.user.first_name} ${note.user.last_name}`}
                              </span>
                            )}
                          </td>
                          <td>
                            <div className={styles.actionButtons}>
                              {/* Ticket #310: only the note's author may edit or remove it. */}
                              {note.user_id === user?.id && (
                                <button
                                  type="button"
                                  className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('Edit note button clicked, navigating to:', `/tickets/${ticket.id}/notes/${note.id}/edit`);
                                    navigate(`/tickets/${ticket.id}/notes/${note.id}/edit`);
                                  }}
                                  title="Edit note"
                                >
                                  Edit
                                </button>
                              )}
                              {note.user_id === user?.id && (
                                <button
                                  type="button"
                                  className={`${styles.btn} ${styles.btnDanger} ${styles.btnSm}`}
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    handleRemoveNote(note);
                                  }}
                                  title="Remove note with all related records"
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="3" className={styles.noData}>
                          No notes
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Contractors' Decision - Hidden for guardians */}
          {!isGuardianUser && (
            <div className={styles.panel} style={{ border: "none" }}>
              <div className={styles.panelTitle} style={{ border: "none" }}>
                <h3>Contractors' decision</h3>
              </div>
              <div className={styles.panelGroup} style={{ border: "none" }}>
                {ticket.history && ticket.history.length > 0 ? (
                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Contractor</th>
                        <th>Status</th>
                        <th>Updated</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ticket.history.map((history, index) => (
                        <tr key={index}>
                          <td>
                            {history.contractorContact?.user?.fullName || "N/A"}
                          </td>
                          <td>{history.status}</td>
                          <td>{formatDateTime(history.updated_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className={styles.noData}>No decision</div>
                )}
              </div>
            </div>
          )}

          {/* Certificate upload & close — contractor submits cert + completes ticket */}
          <TicketCertificatePanel ticket={ticket} onSubmitted={loadTicketData} />
        </div>

        {/* Right Column */}
        <div className={styles.rightColumn}>
          {/* Ticket Reported By System, Staff Member, or Guardian */}
          <div className={styles.panel}>
            <div className={styles.panelTitle}>
              <h3>
                {ticket.guardian ? 'Ticket reported by Guardian' : 
                 ticket.member ? 'Ticket reported by Staff member' : 
                 'Ticket reported by the system'}
              </h3>
            </div>
            <div className={styles.panelGroup}>
              <dl className={styles.detailsList}>
                {ticket.guardian ? (
                  // Guardian reported ticket
                  <>
                    <div>
                      <dt>Name:</dt>
                      <dd>
                        {ticket.guardian?.user?.first_name && ticket.guardian?.user?.last_name
                          ? `${ticket.guardian.user.first_name} ${ticket.guardian.user.last_name}`
                          : ticket.guardianUser?.first_name && ticket.guardianUser?.last_name
                          ? `${ticket.guardianUser.first_name} ${ticket.guardianUser.last_name}`
                          : ticket.guardian?.full_name
                          ? ticket.guardian.full_name
                          : ticket.guardian?.first_name && ticket.guardian?.last_name
                          ? `${ticket.guardian.first_name} ${ticket.guardian.last_name}`
                          : "-"}
                      </dd>
                    </div>
                    <div>
                      <dt>Phone number:</dt>
                      <dd>{ticket.guardian?.user?.phone_number || ticket.guardianUser?.phone_number || ticket.guardian?.phone_number || "-"}</dd>
                    </div>
                    <div>
                      <dt>E-mail:</dt>
                      <dd>{ticket.guardian?.user?.email || ticket.guardianUser?.email || ticket.guardian?.email || "-"}</dd>
                    </div>
                  </>
                ) : ticket.member ? (
                  // Staff member reported ticket
                  <>
                    <div>
                      <dt>Name:</dt>
                      <dd>{ticket.member ? `${ticket.member.first_name} ${ticket.member.last_name}` : "-"}</dd>
                    </div>
                    <div>
                      <dt>Phone number:</dt>
                      <dd>{ticket.member?.phone_number || "-"}</dd>
                    </div>
                    <div>
                      <dt>E-mail:</dt>
                      <dd>{ticket.member?.email || "-"}</dd>
                    </div>
                  </>
                ) : (
                  // System reported ticket - empty section
                  <></>
                )}
              </dl>
            </div>
          </div>

          {/* Ticket Assigned To - Hidden for guardians */}
          {!isGuardianUser && ticket.assignedMember && (
            <div className={styles.panel}>
              <div className={styles.panelTitle}>
                <h3>Ticket assigned to</h3>
              </div>
              <div className={styles.panelGroup}>
                <dl className={styles.detailsList}>
                  <div>
                    <dt>Name:</dt>
                    <dd>{ticket.assignedMember ? `${ticket.assignedMember.first_name} ${ticket.assignedMember.last_name}` : "-"}</dd>
                  </div>
                  <div>
                    <dt>Phone number:</dt>
                    <dd>{ticket.assignedMember?.phone_number || "-"}</dd>
                  </div>
                  <div>
                    <dt>E-mail:</dt>
                    <dd>{ticket.assignedMember?.email || "-"}</dd>
                  </div>
                </dl>
              </div>
            </div>
          )}

          {/* Dates */}
          <div className={styles.panel}>
            <div className={styles.panelTitle}>
              <h3>Dates</h3>
            </div>
            <div className={styles.panelGroup}>
              <dl className={styles.detailsList}>
                <div>
                  <dt>Submitted:</dt>
                  <dd>{formatDate(ticket.created_at)}</dd>
                </div>

                {/* Staff-only date fields - Hidden for guardians */}
                {!isGuardianUser && (
                  <>
                    <div>
                      <dt>Book in day:</dt>
                      <dd>{ticket.book_in_date ? formatDate(ticket.book_in_date) : "-"}</dd>
                    </div>

                    <div>
                      <dt>Book in time from:</dt>
                      <dd>{ticket.book_in_date ? formatTime(ticket.book_in_date) : "00:00"}</dd>
                    </div>

                    <div>
                      <dt>Book in time to:</dt>
                      <dd>{ticket.book_in_date_to ? formatTime(ticket.book_in_date_to) : "00:00"}</dd>
                    </div>

                    {/* The deadline used to be visible only behind "Edit
                        Ticket" (client request, Sept 2026). */}
                    <div>
                      <dt>Deadline:</dt>
                      <dd>
                        {ticket.date_estimated_completion
                          ? formatDate(ticket.date_estimated_completion)
                          : "-"}
                      </dd>
                    </div>

                    {/* When the ticket first moved into Contractor Completed.
                        Blank for older tickets where the change predates our
                        history — deliberately not substituted with another
                        date (client request). */}
                    <div>
                      <dt>Contractor completed:</dt>
                      <dd
                        title={
                          ticket.contractor_completed_at
                            ? "When this ticket first moved into Contractor Completed"
                            : "Not recorded for this ticket"
                        }
                      >
                        {ticket.contractor_completed_at
                          ? formatDate(ticket.contractor_completed_at)
                          : "-"}
                      </dd>
                    </div>

                    <div>
                      <dt>Completed:</dt>
                      <dd>
                        {ticket.date_completed
                          ? formatDate(ticket.date_completed)
                          : "-"}
                      </dd>
                    </div>
                  </>
                )}
              </dl>
            </div>
          </div>

          {/* Contractor Contact Person - Hidden for guardians */}
          {!isGuardianUser && (
            <div className={styles.panel}>
              <div
                className={`${styles.panelTitle} ${styles.contractorContactTitle}`}
              >
                <h3>Contractor contact person</h3>
              </div>
              <div className={styles.panelGroup}>
                {ticket.contractorContact ? (
                  <dl className={styles.detailsList}>
                    <div>
                      <dt>Contractor:</dt>
                      <dd>{ticket.contractor?.name || "-"}</dd>
                    </div>

                    {ticket.contractorContact ? (
                      <>
                        <div>
                          <dt>Contact person:</dt>
                          <dd>{ticket.contractorContact.user ? `${ticket.contractorContact.user.first_name} ${ticket.contractorContact.user.last_name}` : `${ticket.contractorContact.department || ''} - ${ticket.contractorContact.description || ''}`}</dd>
                        </div>

                        <div>
                          <dt>Phone number:</dt>
                          <dd>{ticket.contractorContact.user?.phone_number || "-"}</dd>
                        </div>

                        <div>
                          <dt>Mobile number:</dt>
                          <dd>{ticket.contractorContact.user?.mobile_number || "-"}</dd>
                        </div>

                        <div>
                          <dt>E-mail:</dt>
                          <dd>{ticket.contractorContact.user?.email || "-"}</dd>
                        </div>
                      </>
                    ) : (
                      <div>
                        <dt>Contact person:</dt>
                        <dd>Not assigned</dd>
                      </div>
                    )}
                  </dl>
                ) : (
                  <div className={styles.noData}>No contractor contact!</div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketShow;
